import { CardConfig } from '@gfe/shared/components/cards-list/cards-list.component';

/**
 * Orders entity cards for the grid.
 *
 * Cards follow the user's manual `order` (an array of entity names/ids). Cards
 * absent from `order` come after the ordered ones, sorted alphabetically by
 * title (locale-aware) so newly added entities land predictably at the end
 * rather than at an arbitrary spot. Pinning does not move a card: pinned cards
 * keep their place and are surfaced separately as shortcuts.
 *
 * Pure and dependency-free so it can be unit-tested without the Angular/Karma
 * harness. The input array is not mutated (a copy is sorted).
 */
export function sortEntityCards(
  cards: CardConfig[],
  order: string[],
  lang: string
): CardConfig[] {
  const orderIndex = new Map<string, number>();
  order.forEach((id, index) => {
    // First occurrence wins if a name were ever duplicated in the stored order.
    if (!orderIndex.has(id)) orderIndex.set(id, index);
  });

  const rank = (card: CardConfig): number =>
    orderIndex.has(card.id) ? (orderIndex.get(card.id) as number) : Infinity;

  return [...cards].sort((a, b) => {
    const rankA = rank(a);
    const rankB = rank(b);
    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // Both unranked (rankA === rankB === Infinity): alphabetical fallback.
    return a.title.localeCompare(b.title, lang || undefined, { sensitivity: 'base' });
  });
}
